import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Close,
  Save,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiService } from '../../services/apiService';

const EditSupplier: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [formData, setFormData] = useState({
    supplier_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    supplier_type_id: '',
    payment_type_id: '',
    notes: '',
  });
  const [supplierTypes, setSupplierTypes] = useState<any[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (id) {
      fetchSupplierData();
      fetchDropdownData();
    }
  }, [id]);

  const fetchSupplierData = async () => {
    try {
      setFetching(true);
      const response = await apiService.getSupplier(parseInt(id!));
      const supplier = response.data;
      
      setFormData({
        supplier_name: supplier.supplier_name || '',
        contact_person: supplier.contact_person || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        supplier_type_id: supplier.supplier_type?.id?.toString() || '',
        payment_type_id: supplier.payment_type?.id?.toString() || '',
        notes: supplier.notes || '',
      });
    } catch (error: any) {
      console.error('Failed to fetch supplier data:', error);
      toast.error('Failed to fetch supplier data');
      navigate('/suppliers');
    } finally {
      setFetching(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [supplierTypesResponse, paymentTypesResponse] = await Promise.all([
        apiService.getSupplierTypes(),
        apiService.getPaymentTypes(),
      ]);
      
      setSupplierTypes(supplierTypesResponse.data || []);
      setPaymentTypes(paymentTypesResponse.data || []);
    } catch (error: any) {
      console.error('Failed to fetch dropdown data:', error);
      toast.error('Failed to load dropdown data');
    }
  };

  const handleInputChange = (field: string) => (event: any) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      const submitData = {
        ...formData,
        name: formData.supplier_name, // Send supplier_name as name for backend validation
        supplier_type_id: parseInt(formData.supplier_type_id),
        payment_type_id: parseInt(formData.payment_type_id),
      };

      await apiService.updateSupplier(parseInt(id!), submitData);
      toast.success('Supplier updated successfully!');
      navigate('/suppliers');
    } catch (error: any) {
      console.error('Failed to update supplier:', error);
      toast.error(error.message || 'Failed to update supplier');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={true}
      onClose={() => navigate('/suppliers')}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: 'primary.main', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Edit Supplier
        </Typography>
        <IconButton
          onClick={() => navigate('/suppliers')}
          sx={{ color: 'white' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Supplier Name"
                placeholder="Enter"
                value={formData.supplier_name}
                onChange={handleInputChange('supplier_name')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Contact Person"
                placeholder="Enter"
                value={formData.contact_person}
                onChange={handleInputChange('contact_person')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email Address"
                placeholder="Enter"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Phone Number"
                placeholder="Enter"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address"
                placeholder="Enter"
                multiline
                rows={3}
                value={formData.address}
                onChange={handleInputChange('address')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Supplier Type</InputLabel>
                <Select
                  value={formData.supplier_type_id}
                  label="Supplier Type"
                  onChange={handleInputChange('supplier_type_id')}
                >
                  {supplierTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Payment Type</InputLabel>
                <Select
                  value={formData.payment_type_id}
                  label="Payment Type"
                  onChange={handleInputChange('payment_type_id')}
                >
                  {paymentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                placeholder="Enter"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange('notes')}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={() => navigate('/suppliers')}
          sx={{ px: 3 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<Save />}
          disabled={loading || fetching}
          sx={{ px: 3 }}
        >
          {loading ? 'Updating...' : 'Edit Supplier'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSupplier;
